import api, { handleResponse, handleError } from './api';
import { UPLOAD_BASE } from '../utils/apiBase';

/**
 * Portals-as-stories — the rail, the reels, posting to them and watching them.
 * Backed by /api/stories. A Portal's clip and an ordinary story are the same
 * thing here; the reel is [the Portal] + [their stories].
 */
const storyService = {
  /** What the device is allowed to post: { videoUpload, maxDurationSec, maxBytes, dailyLimit }. */
  async getCapabilities() {
    try { return handleResponse(await api.get('/stories/capabilities')); }
    catch (e) { return handleError(e); }
  },

  /** The rail. Anonymous callers get public Portals only — never anyone's stories. */
  async getReels({ lat = null, lng = null, limit = 50, clubId = null, introductions = false, city = null } = {}) {
    const params = { limit };
    // A city's rail also carries introductions, which reach that town and nowhere else.
    if (introductions) { params.introductions = true; if (city) params.city = city; }
    if (lat != null) params.lat = lat;
    if (lng != null) params.lng = lng;
    // Scoped to one club: its Portals, for its members.
    if (clubId) params.clubId = clubId;
    try { return handleResponse(await api.get('/stories/reels', { params })); }
    catch (e) { return handleError(e); }
  },

  /** INTRODUCE YOURSELF: seven days on your city's rail. Pressing it twice is the same one. */
  async introduce({ lat = null, lng = null } = {}) {
    try { return handleResponse(await api.post('/stories/introduction', { latitude: lat, longitude: lng })); }
    catch (e) { return handleError(e); }
  },

  /** { introduction, canIntroduce } for the signed-in account. */
  async getMyIntroduction() {
    try { return handleResponse(await api.get('/stories/introduction/me')); }
    catch (e) { return handleError(e); }
  },

  async getReel(userId, { lat = null, lng = null } = {}) {
    const params = {};
    if (lat != null) params.lat = lat;
    if (lng != null) params.lng = lng;
    try { return handleResponse(await api.get(`/stories/reels/${userId}`, { params })); }
    catch (e) { return handleError(e); }
  },

  /**
   * Upload a clip. Returns a DRAFT story — invisible to everyone until publish().
   * That is what lets the composer shoot first and fill in the details second:
   * the file encodes while they type.
   */
  async uploadVideo(file, { onProgress = null } = {}) {
    const form = new FormData();
    form.append('file', file);
    try {
      return handleResponse(await api.post('/stories/video', form, {
        baseURL: UPLOAD_BASE,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (e) => onProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0)
          : undefined,
      }));
    } catch (e) { return handleError(e); }
  },

  async uploadImage(file) {
    const form = new FormData();
    form.append('file', file);
    try {
      return handleResponse(await api.post('/stories/image', form, {
        baseURL: UPLOAD_BASE,
        headers: { 'Content-Type': 'multipart/form-data' },
      }));
    } catch (e) { return handleError(e); }
  },

  /** req: { caption, overlay, audience, audienceUserIds, signalEventId, latitude, longitude } */
  async publish(storyId, req) {
    try { return handleResponse(await api.post(`/stories/${storyId}/publish`, req)); }
    catch (e) { return handleError(e); }
  },

  /** Poll while the composer is open; the server keeps promoting after they leave. */
  async refresh(storyId) {
    try { return handleResponse(await api.post(`/stories/${storyId}/refresh`)); }
    catch (e) { return handleError(e); }
  },

  /**
   * Re-frame, re-aim or re-cut a clip that is already up, without uploading it again.
   * req: { frame, resetFrame, coverFocus, trimStartMs, trimEndMs } — only what is sent changes.
   */
  async update(storyId, req) {
    try { return handleResponse(await api.put(`/stories/${storyId}`, req)); }
    catch (e) { return handleError(e); }
  },

  async remove(storyId) {
    try { return handleResponse(await api.delete(`/stories/${storyId}`)); }
    catch (e) { return handleError(e); }
  },

  /**
   * Mark what was watched. Batched deliberately: a fast swipe through six items
   * is one request on the way out, not six mid-gesture.
   * marks: [{ itemType: 'portal'|'story', itemId, completed }]
   */
  async markViewed(marks) {
    if (!marks || marks.length === 0) return { success: true };
    try { return handleResponse(await api.post('/stories/views', { marks })); }
    catch (e) { return handleError(e); }
  },

  /** How many watched. Who watched is never returned — by design, not omission. */
  async getSeenCount(itemId, itemType = 'story') {
    try { return handleResponse(await api.get(`/stories/${itemId}/seen-count`, { params: { itemType } })); }
    catch (e) { return handleError(e); }
  },

  /** Who's down and who viewed your Portal: { down: [...], views: [...] }. The host's only. */
  async getActivity(portalId) {
    try { return handleResponse(await api.get(`/stories/${portalId}/activity`)); }
    catch (e) { return handleError(e); }
  },

  /** Ring state for a batch of people — see PortalPresenceContext. */
  async getPresence(userIds) {
    if (!userIds || userIds.length === 0) return { success: true, data: {} };
    try { return handleResponse(await api.post('/stories/presence', { userIds })); }
    catch (e) { return handleError(e); }
  },

  async mute(userId) {
    try { return handleResponse(await api.post(`/stories/mutes/${userId}`)); }
    catch (e) { return handleError(e); }
  },

  async unmute(userId) {
    try { return handleResponse(await api.delete(`/stories/mutes/${userId}`)); }
    catch (e) { return handleError(e); }
  },
};

export default storyService;
